
import { Card, CardContent } from "@/components/ui/card";
import { StarIcon, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

type TestimonialProps = {
  content: string;
  author: string;
  date: string;
  highlight?: string; // Add optional highlight prop
};

const Testimonial = ({
  content,
  author,
  date,
  highlight
}: TestimonialProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Function to highlight text if a highlight phrase is provided
  const renderHighlightedContent = () => {
    if (!highlight || !content.includes(highlight)) {
      return <p className="text-gray-200 italic leading-relaxed text-sm md:text-base">{content}</p>;
    }
    const parts = content.split(highlight);
    return <p className="text-gray-200 italic leading-relaxed text-sm md:text-base">
        {parts[0]}
        <span className="text-[#33C3F0] font-medium">{highlight}</span>
        {parts[1]}
      </p>;
  };

  // Get a preview of the content (first 150 characters)
  const previewContent = () => {
    if (!highlight || !content.includes(highlight)) {
      const preview = content.substring(0, 150) + (content.length > 150 ? "..." : "");
      return <p className="text-gray-200 italic leading-relaxed text-sm md:text-base">{preview}</p>;
    }
    
    // If there's a highlight and it's within the first 150 chars, include it in preview
    const parts = content.split(highlight);
    const previewFirstPart = parts[0].substring(0, 150);
    
    if (previewFirstPart.length < 150) {
      // We can include the highlight and some of the second part
      const remainingChars = 150 - previewFirstPart.length;
      const previewSecondPart = parts[1].substring(0, remainingChars) + (parts[1].length > remainingChars ? "..." : "");
      
      return <p className="text-gray-200 italic leading-relaxed text-sm md:text-base">
        {previewFirstPart}
        <span className="text-[#33C3F0] font-medium">{highlight}</span>
        {previewSecondPart}
      </p>;
    } else {
      // Just show first part with ellipsis
      return <p className="text-gray-200 italic leading-relaxed text-sm md:text-base">
        {previewFirstPart.substring(0, 147)}...
      </p>;
    }
  };

  return (
    <Card className="bg-black/60 border border-gray-700 backdrop-blur-md h-full transition-all duration-300 hover:shadow-xl hover:border-gray-500 rounded-lg shadow-md shadow-blue-900/20">
      <CardContent className="p-6 flex flex-col h-full">
        <div className="flex mb-4">
          {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-[#FF3366] fill-[#FF3366]" />)}
        </div>
        
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className="mb-6 flex-grow">
          {!isExpanded && (
            <div className="mb-2">
              {previewContent()}
            </div>
          )}
          <CollapsibleContent>
            {renderHighlightedContent()}
          </CollapsibleContent>
          
          <CollapsibleTrigger className="flex items-center text-[#33C3F0] text-sm font-medium hover:text-blue-400 transition-colors mt-2">
            {isExpanded ? (
              <>
                <span>Read less</span>
                <ChevronUp className="ml-1 h-4 w-4" />
              </>
            ) : (
              <>
                <span>Read more</span>
                <ChevronDown className="ml-1 h-4 w-4" />
              </>
            )}
          </CollapsibleTrigger>
        </Collapsible>
        
        <div className="mt-auto">
          <p className="text-white font-medium">{author}</p>
          <p className="text-gray-400 text-sm">{date}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const Testimonials = () => {
  const testimonials = [
    {
      content: "I remember Mathew telling me about Crypto about 8 years ago and it all going completely over my head. In hindsight I wish I'd have paid more attention. Being quite averse to new tech and having little patience for learning new things I failed a couple of years ago to sign up to some exchanges and gave up. Fortunately, I recently reconnected with Matthew upon him moving to the eastern province and he once again brought up the topic of crypto and offered a course which he was testing. I signed up and initially the course was tough going due to the amount of information needed to take in to get to grips with using exchanges, transferring funds, and getting approvals etc, but with the help of Matthew's infectious enthusiasm for the topic I found myself quickly gaining knowledge and interest in what I was partaking in. I'm glad I took the risk as the initial investment of paying for the course and subscription to Bitsgap ended up being recouped in less than a week. I've also found that crypto investment has now become somewhat of a hobby as well as a great way to make passive income. I would highly recommend taking the course with Mathew, especially if like myself you've had money sitting doing nothing for years on end.",
      author: "Andrew Birch",
      date: "11th May 2024",
      highlight: "the initial investment of paying for the course and subscription to Bitsgap ended up being recouped in less than a week"
    }, 
    {
      content: "We have been very fortunate to receive a training programme from Mehdi (Matthew), who has exceptional knowledge on trading within cryptocurrency and the use of crypto trading bots. Prior to this we really didn't know anything beyond traditional banking systems and stocks and shares. We were keen to understand how we could use ethical investments in the crypto market to increase our income. Through Mehdi's (Matthew's) tutorials we have transformed our approach to investing, and we haven't looked back. Our investments have increased by over 25% return per month. Mehdi has spent a colossal amount of time in researching market trends and shares this knowledge which he has painstakingly gathered so that we can benefit. His patience and thoroughness in explaining complex concepts is truly commendable. He ensured that what we learned would empower us as investors, giving us the confidence and tools needed to explore the market for new opportunities by ourselves. Now, we feel empowered to be the captains of our own ship, taking steps towards a secure financial future.",
      author: "Dr. Anjuman & Julio, Edinburgh UK",
      date: "February 5th 2025",
      highlight: "Our investments have increased by over 25% return per month"
    }
  ];
  
  return (
    <section id="testimonials" className="bg-gradient-to-b from-black/70 to-black/90 py-20">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-6">The system that helps you earn more in monthly yield than traditional finance offers yearly</h2>
        <h3 className="text-2xl md:text-3xl font-bold text-center text-white mb-8">Here's What Our Graduates Are Saying...</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <Testimonial 
              key={index} 
              content={testimonial.content} 
              author={testimonial.author} 
              date={testimonial.date} 
              highlight={testimonial.highlight} 
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
